import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { ArrowLeft, Loader2, Lock, CheckCircle2, Eye, AlertTriangle, Undo2, RefreshCw, Printer, ShieldCheck, RotateCcw, Download } from 'lucide-react';
import { toast } from 'sonner';
import { StatusBadge, GuiaStatusBadge, GuiaViewer } from './GuiaViewer';
import { Comprovante } from './Comprovante';
import { errMsg, fmtDate, fmtDateTime, TIPO_DIVERGENCIA_LABELS, VALIDACAO_STATUS } from './guiasUtils';
import { Badge } from '../ui/badge';

const Stat = ({ label, value, cls = '' }) => (
  <div className="rounded-lg border bg-card p-3">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`text-xl font-bold ${cls}`} data-testid={`conf-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>{value}</p>
  </div>
);

export const ConferenciaEntrega = ({ entregaId, onBack, onChanged }) => {
  const { api, user, can } = useAuth();
  const [det, setDet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewGuia, setViewGuia] = useState(null);
  const [divPac, setDivPac] = useState(null);
  const [divForm, setDivForm] = useState({ tipo: 'guia_ilegivel', descricao: '' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [devolverOpen, setDevolverOpen] = useState(false);
  const [devolverGuia, setDevolverGuia] = useState(null);
  const [motivoGuia, setMotivoGuia] = useState('');
  const [histGuia, setHistGuia] = useState(null);
  const [reabrirOpen, setReabrirOpen] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [busy, setBusy] = useState(false);
  const [comprovante, setComprovante] = useState(null);
  const [isMaster, setIsMaster] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const baixarTodas = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/guias/recebimento/entregas/${entregaId}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `guias_${det?.entrega?.numero_recebimento || entregaId}_${det?.entrega?.competencia || ''}.zip`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success('Download das guias iniciado');
    } catch (e) { toast.error(errMsg(e, 'Erro ao baixar guias')); }
    finally { setDownloading(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/guias/recebimento/entregas/${entregaId}`);
      setDet(r.data);
    } catch (e) { toast.error(errMsg(e, 'Erro ao carregar entrega')); }
    finally { setLoading(false); }
  }, [api, entregaId]);

  useEffect(() => { load(); api.get('/guias/meta').then((r) => setIsMaster(r.data.is_master)); }, [load, api]);

  const refresh = () => { load(); onChanged?.(); };

  const toggleConferida = async (guia, value) => {
    try {
      await api.post(`/guias/recebimento/guias/${guia.id}/conferir`, { conferida: value });
      refresh();
    } catch (e) { toast.error(errMsg(e, 'Erro ao marcar conferência')); }
  };

  const validarGuia = async (guia, acao, motivo) => {
    try {
      await api.post(`/guias/recebimento/guias/${guia.id}/validar`, { acao, motivo: motivo || null });
      toast.success({ receber: 'Guia marcada como recebida', aprovar: 'Guia aprovada', devolver: 'Guia devolvida para correção' }[acao]);
      setDevolverGuia(null); setMotivoGuia(''); refresh();
    } catch (e) { toast.error(errMsg(e, 'Erro ao validar guia')); }
  };

  const registrarDivergencia = async () => {
    setBusy(true);
    try {
      await api.post(`/guias/recebimento/entregas/${entregaId}/divergencias`, {
        paciente_id: divPac.paciente_id, paciente_nome: divPac.paciente_nome, guia_id: divPac.guia?.id || null, ...divForm,
      });
      toast.success('Divergência registrada');
      setDivPac(null); setDivForm({ tipo: 'guia_ilegivel', descricao: '' }); refresh();
    } catch (e) { toast.error(errMsg(e, 'Erro ao registrar divergência')); }
    finally { setBusy(false); }
  };

  const resolver = async (d) => {
    try { await api.put(`/guias/recebimento/divergencias/${d.id}/resolver`, { observacao: 'Resolvida na conferência' }); toast.success('Divergência resolvida'); refresh(); }
    catch (e) { toast.error(errMsg(e, 'Erro ao resolver')); }
  };

  const confirmar = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/guias/recebimento/entregas/${entregaId}/confirmar`);
      toast.success(`Recebimento concluído — ${r.data.numero_recebimento}`);
      setConfirmOpen(false); refresh();
      const c = await api.get(`/guias/recebimento/entregas/${entregaId}/comprovante`);
      setComprovante(c.data);
    } catch (e) { toast.error(errMsg(e, 'Recebimento não pode ser confirmado')); }
    finally { setBusy(false); }
  };

  const devolver = async () => {
    setBusy(true);
    try { await api.post(`/guias/recebimento/entregas/${entregaId}/devolver`, { motivo }); toast.success('Entrega devolvida ao profissional'); setDevolverOpen(false); setMotivo(''); refresh(); }
    catch (e) { toast.error(errMsg(e, 'Erro ao devolver')); }
    finally { setBusy(false); }
  };

  const reabrir = async () => {
    setBusy(true);
    try { await api.post(`/guias/recebimento/entregas/${entregaId}/reabrir`, { motivo }); toast.success('Recebimento reaberto (auditado)'); setReabrirOpen(false); setMotivo(''); refresh(); }
    catch (e) { toast.error(errMsg(e, 'Erro ao reabrir')); }
    finally { setBusy(false); }
  };

  if (loading && !det) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  if (!det) return null;

  const e = det.entrega;
  const emConferencia = ['aguardando_conferencia', 'em_conferencia', 'com_divergencia'].includes(e.status) && can('conferir_guias');
  const recebido = e.status === 'recebido' && can('ver_comprovante_recebimento');
  const podeDivergencia = can('registrar_divergencia');
  const podeDevolver = can('devolver_recebimento');
  const podeConfirmarRec = can('confirmar_recebimento');
  const bloqueadoMesmoUsuario = det.mesmo_usuario_entregou;
  const divsAbertas = det.divergencias.filter((d) => d.status === 'aberta');

  return (
    <div className="space-y-4" data-testid="conferencia-panel">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="btn-voltar-entregas"><ArrowLeft className="h-4 w-4 mr-1" />Voltar</Button>
        <div className="flex-1 min-w-[200px]">
          <h2 className="text-xl font-bold leading-tight">Conferência de Guias — {e.profissional_nome}</h2>
          <p className="text-sm text-muted-foreground">{e.categoria_label} · Competência {fmtDate(e.competencia)} · Entregue por {e.entregue_por_nome} em {fmtDateTime(e.entregue_em)}</p>
        </div>
        <StatusBadge status={e.status} testId="entrega-status" />
        {recebido && <Button variant="outline" size="sm" onClick={baixarTodas} disabled={downloading} data-testid="btn-baixar-todas"><Download className={`h-4 w-4 mr-1 ${downloading ? 'animate-bounce' : ''}`} />Baixar todas ({det.lancadas})</Button>}
        {recebido && <Button variant="outline" size="sm" onClick={async () => setComprovante((await api.get(`/guias/recebimento/entregas/${entregaId}/comprovante`)).data)} data-testid="btn-comprovante"><Printer className="h-4 w-4 mr-1" />Comprovante</Button>}
        <Button variant="outline" size="icon" onClick={load}><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
      </div>

      {det.multi ? (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="conf-stats-especialidade">
          <Stat label="Pacientes vinculados" value={det.pacientes_total} />
          <Stat label="Guias esperadas" value={det.esperadas_definida ? det.esperadas : 'Não definida'} cls={det.esperadas_definida ? '' : 'text-sm text-muted-foreground'} />
          <Stat label="Guias enviadas" value={det.lancadas} />
          <Stat label="Recebidas" value={det.recebidas} cls="text-blue-600" />
          <Stat label="Aprovadas" value={`${det.aprovadas}/${det.validas}`} cls={det.aprovadas === det.validas && det.validas > 0 ? 'text-green-600' : ''} />
          <Stat label="Devolvidas" value={det.devolvidas} cls={det.devolvidas > 0 ? 'text-red-600' : ''} />
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat label="Pacientes da competência" value={det.esperadas} />
          <Stat label="Guias lançadas" value={det.lancadas} cls={det.lancadas !== det.esperadas ? 'text-red-600' : ''} />
          <Stat label="Guias conferidas" value={`${det.conferidas}/${det.esperadas}`} cls={det.conferidas === det.esperadas && det.esperadas > 0 ? 'text-green-600' : 'text-blue-600'} />
          <Stat label="Pendências" value={det.pendentes + det.inconsistentes} cls={det.pendentes + det.inconsistentes > 0 ? 'text-red-600' : 'text-green-600'} />
          <Stat label="Divergências" value={divsAbertas.length} cls={divsAbertas.length > 0 ? 'text-orange-600' : ''} />
        </div>
      )}
      {det.multi && !det.pode_confirmar_recebimento && emConferencia && (
        <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/20"><CardContent className="p-3 text-sm flex gap-2" data-testid="alerta-validacao-pendente">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
          <div><b>Recebimento só pode ser confirmado com todas as guias aprovadas.</b> Pendentes de validação: {det.pendentes_validacao} · Devolvidas aguardando reenvio: {det.devolvidas}{det.inconsistentes > 0 ? ` · ${det.inconsistentes} guia(s) de paciente fora da carteira` : ''}. Um documento enviado não é considerado aprovado automaticamente.</div>
        </CardContent></Card>
      )}

      {!det.multi && (det.pendentes > 0 || det.inconsistentes > 0) && (
        <Card className="border-red-300 bg-red-50 dark:bg-red-950/20"><CardContent className="p-3 text-sm flex gap-2" data-testid="alerta-recebimento-bloqueado">
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
          <div><b>RECEBIMENTO NÃO PODE SER CONFIRMADO.</b> Pacientes esperados: {det.esperadas} · Guias apresentadas: {det.lancadas} · Pendência: {det.pendentes} guia(s){det.inconsistentes > 0 ? ` · ${det.inconsistentes} guia(s) de paciente não ativo` : ''}. A carteira considerada é a da data da competência — devolva ao profissional para correção.</div>
        </CardContent></Card>
      )}
      {bloqueadoMesmoUsuario && emConferencia && (
        <Card className="border-orange-300 bg-orange-50 dark:bg-orange-950/20"><CardContent className="p-3 text-sm flex gap-2" data-testid="alerta-mesmo-usuario">
          <ShieldCheck className="h-5 w-5 text-orange-600 shrink-0" />Você confirmou a entrega desta remessa. Por segurança, o recebimento deve ser confirmado por outro usuário responsável.
        </CardContent></Card>
      )}

      <Card><CardContent className="p-0 divide-y">
        <div className="hidden sm:grid grid-cols-[40px_1fr_150px_120px_auto] gap-2 px-3 py-2 text-xs font-medium text-muted-foreground">
          <span>Nº</span><span>Paciente</span><span>Situação</span><span>Conferida</span><span>Ações</span>
        </div>
        {det.pacientes.map((pac) => {
          const g = pac.guia;
          const conferida = g && ['conferida', 'recebida_conferida'].includes(g.status);
          if (det.multi) return (
            <div key={pac.paciente_id} className="px-3 py-2.5 space-y-1.5" data-testid={`conf-row-${pac.paciente_id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground">{String(pac.numero).padStart(2, '0')}</span>
                <p className="font-medium text-sm flex-1">{pac.paciente_nome}</p>
                <span className="text-xs text-muted-foreground">{pac.total_guias} guia(s){pac.sessoes_registradas ? ` · ${pac.sessoes_registradas} sessão(ões) registrada(s)` : ''}</span>
                {emConferencia && podeDivergencia && <Button size="sm" variant="ghost" className="h-7 text-orange-700" onClick={() => setDivPac(pac)} data-testid={`btn-divergencia-${pac.paciente_id}`}><AlertTriangle className="h-4 w-4 mr-1" />Divergência</Button>}
              </div>
              {pac.guias.length === 0 && <p className="text-xs text-amber-700 pl-6">Nenhuma guia enviada para este paciente.</p>}
              {pac.guias.map((gg) => {
                const v = VALIDACAO_STATUS[gg.validacao] || VALIDACAO_STATUS.pendente;
                return (
                  <div key={gg.id} className="ml-6 flex flex-wrap items-center gap-2 rounded border px-2 py-1.5 text-xs" data-testid={`conf-guia-${gg.id}`}>
                    <span className="font-medium">{fmtDate(gg.data_atendimento)}</span>
                    <Badge variant="outline" className="text-[10px]">{gg.tipo_guia_label}</Badge>
                    <span className="text-muted-foreground">{gg.tipo_documento_label}</span>
                    {gg.observacao && <span className="text-muted-foreground italic truncate max-w-[200px]" title={gg.observacao}>“{gg.observacao}”</span>}
                    <Badge className={`text-[10px] ${v.cls}`} data-testid={`conf-validacao-${gg.id}`}>{v.label}</Badge>
                    {gg.validacao === 'devolvida' && gg.motivo_devolucao && <span className="text-red-700 w-full ml-0">Motivo: {gg.motivo_devolucao}</span>}
                    <span className="ml-auto flex gap-1">
                      <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => setViewGuia(gg.id)} data-testid={`btn-conf-ver-${gg.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2" title="Histórico de validações" onClick={() => setHistGuia(gg)} data-testid={`btn-hist-${gg.id}`}>Histórico</Button>
                      {emConferencia && gg.validacao !== 'aprovada' && gg.validacao !== 'devolvida' && (gg.validacao || 'pendente') === 'pendente' && <Button size="sm" variant="outline" className="h-7 px-2 border-blue-300 text-blue-700" onClick={() => validarGuia(gg, 'receber')} data-testid={`btn-receber-${gg.id}`}>Receber</Button>}
                      {emConferencia && gg.validacao !== 'aprovada' && gg.validacao !== 'devolvida' && <Button size="sm" className="h-7 px-2 bg-green-600 hover:bg-green-700" onClick={() => validarGuia(gg, 'aprovar')} data-testid={`btn-aprovar-${gg.id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Aprovar</Button>}
                      {emConferencia && gg.validacao !== 'devolvida' && can('devolver_recebimento') && <Button size="sm" variant="outline" className="h-7 px-2 border-red-300 text-red-700" onClick={() => setDevolverGuia(gg)} data-testid={`btn-devolver-guia-${gg.id}`}><Undo2 className="h-3.5 w-3.5 mr-1" />Devolver</Button>}
                    </span>
                  </div>
                );
              })}
            </div>
          );
          return (
            <div key={pac.paciente_id} className="grid grid-cols-1 sm:grid-cols-[40px_1fr_150px_120px_auto] gap-2 items-center px-3 py-2.5" data-testid={`conf-row-${pac.paciente_id}`}>
              <span className="text-xs text-muted-foreground hidden sm:block">{String(pac.numero).padStart(2, '0')}</span>
              <div className="min-w-0">
                <p className="font-medium text-sm truncate">{pac.paciente_nome}</p>
                {pac.vinculo_desde && <p className="text-[11px] text-muted-foreground" data-testid={`conf-vinculo-desde-${pac.paciente_id}`}>Desde {fmtDate(pac.vinculo_desde)}</p>}
                {pac.divergencias_abertas > 0 && <p className="text-xs text-orange-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{pac.divergencias_abertas} divergência(s) aberta(s)</p>}
              </div>
              <GuiaStatusBadge status={pac.guia_status} />
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={!!conferida} disabled={!g || !emConferencia || g.status === 'divergencia'} onCheckedChange={(v) => toggleConferida(g, !!v)} data-testid={`chk-conferida-${pac.paciente_id}`} />
                <span className="sm:hidden">Guia conferida</span>
              </label>
              <div className="flex gap-1.5 flex-wrap">
                {g && <Button size="sm" variant="outline" onClick={() => setViewGuia(g.id)} data-testid={`btn-conf-ver-${pac.paciente_id}`}><Eye className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Visualizar</span></Button>}
                {emConferencia && podeDivergencia && <Button size="sm" variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50" onClick={() => setDivPac(pac)} data-testid={`btn-divergencia-${pac.paciente_id}`}><AlertTriangle className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Divergência</span></Button>}
              </div>
            </div>
          );
        })}
      </CardContent></Card>

      {det.divergencias.length > 0 && (
        <Card><CardContent className="p-3 space-y-2" data-testid="lista-divergencias">
          <p className="font-semibold text-sm">Divergências registradas</p>
          {det.divergencias.map((d) => (
            <div key={d.id} className={`rounded border p-2 text-sm flex flex-wrap gap-2 items-start ${d.status === 'aberta' ? 'border-orange-300 bg-orange-50/50 dark:bg-orange-950/10' : 'opacity-70'}`}>
              <div className="flex-1 min-w-[200px]">
                <p><b>{d.paciente_nome}</b> — {TIPO_DIVERGENCIA_LABELS[d.tipo] || d.tipo}</p>
                <p className="text-xs text-muted-foreground">{d.descricao} · {d.registrado_por_nome} · {fmtDateTime(d.registrado_em)}{d.status === 'resolvida' ? ` · Resolvida por ${d.resolvido_por_nome}` : ''}</p>
              </div>
              {d.status === 'aberta' && emConferencia && podeDivergencia && <Button size="sm" variant="outline" onClick={() => resolver(d)} data-testid={`btn-resolver-div-${d.id}`}><CheckCircle2 className="h-4 w-4 mr-1" />Resolver</Button>}
            </div>
          ))}
        </CardContent></Card>
      )}

      {emConferencia && (
        <div className="sticky bottom-3 z-10 flex flex-col sm:flex-row gap-2">
          {podeDevolver && <Button variant="outline" className="h-12" onClick={() => setDevolverOpen(true)} data-testid="btn-devolver-entrega"><Undo2 className="h-4 w-4 mr-2" />Solicitar correção ao profissional</Button>}
          <Button className={`flex-1 h-12 text-base ${det.pode_confirmar_recebimento && !bloqueadoMesmoUsuario ? 'bg-green-600 hover:bg-green-700' : ''}`} disabled={!det.pode_confirmar_recebimento || bloqueadoMesmoUsuario || !podeConfirmarRec} onClick={() => setConfirmOpen(true)} data-testid="btn-confirmar-recebimento">
            {det.pode_confirmar_recebimento && !bloqueadoMesmoUsuario ? <CheckCircle2 className="h-5 w-5 mr-2" /> : <Lock className="h-5 w-5 mr-2" />}
            CONFIRMAR RECEBIMENTO DAS GUIAS
          </Button>
        </div>
      )}
      {recebido && (
        <div className="rounded-lg border-2 border-emerald-500 bg-emerald-50 dark:bg-emerald-950/20 p-4 flex flex-wrap items-center gap-3" data-testid="recebimento-concluido">
          <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          <div className="flex-1"><p className="font-bold text-emerald-800 dark:text-emerald-300">GUIAS RECEBIDAS E CONFERIDAS — {e.numero_recebimento}</p><p className="text-sm">Recebido por {e.recebido_por_nome} em {fmtDateTime(e.recebido_em)} · {e.total_pacientes_recebidos} pacientes · {e.total_guias_recebidas} guias</p></div>
          {isMaster && <Button variant="outline" size="sm" onClick={() => setReabrirOpen(true)} data-testid="btn-reabrir"><RotateCcw className="h-4 w-4 mr-1" />Reabrir</Button>}
        </div>
      )}

      <Dialog open={!!devolverGuia} onOpenChange={(v) => !v && setDevolverGuia(null)}>
        <DialogContent data-testid="devolver-guia-dialog">
          <DialogHeader><DialogTitle>Devolver guia para correção</DialogTitle><DialogDescription>{devolverGuia?.paciente_nome} · atendimento de {fmtDate(devolverGuia?.data_atendimento)}. O profissional poderá reenviar a guia corrigida. Informe o motivo.</DialogDescription></DialogHeader>
          <Textarea value={motivoGuia} onChange={(e) => setMotivoGuia(e.target.value)} placeholder="Motivo da devolução (obrigatório)" data-testid="input-motivo-devolucao-guia" />
          <DialogFooter><Button variant="outline" onClick={() => setDevolverGuia(null)}>Cancelar</Button><Button variant="destructive" disabled={!motivoGuia.trim()} onClick={() => validarGuia(devolverGuia, 'devolver', motivoGuia)} data-testid="btn-confirmar-devolucao-guia">Devolver</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!histGuia} onOpenChange={(v) => !v && setHistGuia(null)}>
        <DialogContent data-testid="historico-guia-dialog">
          <DialogHeader><DialogTitle>Histórico de validações</DialogTitle><DialogDescription>{histGuia?.paciente_nome} · {fmtDate(histGuia?.data_atendimento)} · {histGuia?.tipo_guia_label} · v{histGuia?.versao}</DialogDescription></DialogHeader>
          <ul className="space-y-1 text-sm max-h-[50vh] overflow-y-auto">
            {(histGuia?.historico_validacoes || []).length === 0 && <li className="text-muted-foreground">Sem eventos.</li>}
            {(histGuia?.historico_validacoes || []).map((h, i) => <li key={i} className="rounded border px-2 py-1"><b className="capitalize">{h.acao}</b>{h.para ? ` → ${VALIDACAO_STATUS[h.para]?.label || h.para}` : ''} · {h.por_nome} · {fmtDateTime(h.em)}{h.motivo ? <span className="block text-xs text-red-700">Motivo: {h.motivo}</span> : null}</li>)}
          </ul>
        </DialogContent>
      </Dialog>

      <GuiaViewer guiaId={viewGuia} open={!!viewGuia} onClose={() => setViewGuia(null)}>
        {emConferencia && viewGuia && det.multi && (() => {
          const gg = det.pacientes.flatMap((p) => p.guias || []).find((x) => x.id === viewGuia);
          return gg && gg.validacao !== 'aprovada' && gg.validacao !== 'devolvida' ? (
            <div className="flex flex-wrap gap-2 justify-end pt-2">
              {can('devolver_recebimento') && <Button variant="outline" className="border-red-300 text-red-700" onClick={() => { setViewGuia(null); setDevolverGuia(gg); }} data-testid="viewer-btn-devolver"><Undo2 className="h-4 w-4 mr-1" />Devolver</Button>}
              <Button className="bg-green-600 hover:bg-green-700" onClick={() => { validarGuia(gg, 'aprovar'); setViewGuia(null); }} data-testid="viewer-btn-aprovar"><CheckCircle2 className="h-4 w-4 mr-1" />Aprovar guia</Button>
            </div>
          ) : null;
        })()}
        {emConferencia && viewGuia && !det.multi && (() => {
          const pac = det.pacientes.find((p) => p.guia?.id === viewGuia);
          const conf = pac?.guia && ['conferida', 'recebida_conferida'].includes(pac.guia.status);
          return pac ? (
            <div className="flex flex-wrap gap-2 justify-end pt-2">
              <Button variant="outline" className="border-orange-300 text-orange-700" onClick={() => { setViewGuia(null); setDivPac(pac); }} data-testid="viewer-btn-divergencia"><AlertTriangle className="h-4 w-4 mr-1" />Guia com divergência</Button>
              <Button className="bg-green-600 hover:bg-green-700" disabled={pac.guia.status === 'divergencia'} onClick={() => { toggleConferida(pac.guia, !conf); setViewGuia(null); }} data-testid="viewer-btn-conferir"><CheckCircle2 className="h-4 w-4 mr-1" />{conf ? 'Desmarcar conferida' : 'Guia conferida'}</Button>
            </div>
          ) : null;
        })()}
      </GuiaViewer>

      <Dialog open={!!divPac} onOpenChange={(v) => !v && setDivPac(null)}>
        <DialogContent data-testid="divergencia-dialog">
          <DialogHeader><DialogTitle>Registrar divergência</DialogTitle><DialogDescription>Paciente: <b>{divPac?.paciente_nome}</b></DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Select value={divForm.tipo} onValueChange={(v) => setDivForm((f) => ({ ...f, tipo: v }))}>
              <SelectTrigger data-testid="select-tipo-divergencia"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(TIPO_DIVERGENCIA_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
            <Textarea placeholder="Descrição da divergência (ex.: A guia apresentada fisicamente não corresponde ao paciente)" value={divForm.descricao} onChange={(e) => setDivForm((f) => ({ ...f, descricao: e.target.value }))} data-testid="input-descricao-divergencia" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDivPac(null)}>Cancelar</Button>
            <Button className="bg-orange-600 hover:bg-orange-700" disabled={!divForm.descricao.trim() || busy} onClick={registrarDivergencia} data-testid="btn-salvar-divergencia">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Registrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="confirmar-recebimento-dialog">
          <DialogHeader><DialogTitle>Confirmação de recebimento</DialogTitle><DialogDescription>Revise o resumo antes de encerrar o recebimento.</DialogDescription></DialogHeader>
          <div className="text-sm space-y-1 rounded border p-3 bg-muted/40">
            <p><b>Profissional:</b> {e.profissional_nome}</p><p><b>Categoria:</b> {e.categoria_label}</p>
            <p><b>Pacientes:</b> {det.esperadas} · <b>Guias esperadas:</b> {det.esperadas} · <b>Guias lançadas:</b> {det.lancadas} · <b>Guias conferidas:</b> {det.conferidas} · <b>Divergências:</b> {divsAbertas.length}</p>
          </div>
          <p className="text-sm italic">"Declaro que conferi as guias apresentadas e confirmo o recebimento da documentação correspondente aos {det.esperadas} pacientes relacionados acima."</p>
          <p className="text-xs text-muted-foreground">Responsável: {user?.nome} · A confirmação é registrada com usuário autenticado, data e hora.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancelar</Button>
            <Button className="bg-green-600 hover:bg-green-700" disabled={busy} onClick={confirmar} data-testid="btn-confirmar-recebimento-final">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Confirmar e encerrar recebimento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={devolverOpen || reabrirOpen} onOpenChange={(v) => { if (!v) { setDevolverOpen(false); setReabrirOpen(false); } }}>
        <DialogContent data-testid="motivo-dialog">
          <DialogHeader><DialogTitle>{reabrirOpen ? 'Reabrir recebimento' : 'Solicitar correção ao profissional'}</DialogTitle><DialogDescription>{reabrirOpen ? 'A reabertura fica registrada na auditoria.' : 'A entrega volta para o profissional lançar/corrigir as guias.'}</DialogDescription></DialogHeader>
          <Textarea placeholder="Motivo" value={motivo} onChange={(ev) => setMotivo(ev.target.value)} data-testid="input-motivo" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDevolverOpen(false); setReabrirOpen(false); }}>Cancelar</Button>
            <Button disabled={!motivo.trim() || busy} onClick={reabrirOpen ? reabrir : devolver} data-testid="btn-confirmar-motivo">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Comprovante data={comprovante} onClose={() => setComprovante(null)} />
    </div>
  );
};
